<template>
  <div class="row" v-if="generalTerm">
    <div class="col-12 d-flex align-items-center flex-wrap gap-2">
      <h2 class="mb-0">{{ generalTerm.title }}</h2>
      <span v-if="isLocked" class="badge rounded-pill bg-secondary locked-badge">
        <i class="fa-solid fa-lock me-1"></i>
        {{ $t('general_terms.locked_status') }}
      </span>
    </div>

    <div class="col-12 mt-4">
      <div class="card">
        <div class="card-body">
          <show-row :label="$t('general_terms.id')" :value="String(generalTerm.id)"/>
          <show-row :label="$t('general_terms.key')" :value="generalTerm.key"/>
          <show-row :label="$t('general_terms.title')" :value="generalTerm.title"/>
          <show-row :label="$t('general_terms.created_at')" :value="formatDate(generalTerm.created_at, 'datetime') || '-'"/>
          <show-row :label="$t('general_terms.updated_at')" :value="formatDate(generalTerm.updated_at, 'datetime') || '-'"/>
          <div class="row">
            <div class="col-12 col-lg-3 me-2 text-nowrap">
              {{ $t('general_terms.locked_at') }}
            </div>
            <div class="col-12 col-lg-8 fw-bold">
              <span v-if="isLocked" class="locked-at-value">
                <i class="fa-solid fa-lock text-secondary me-2"></i>
                {{ lockedAtLabel }}
              </span>
              <span v-else class="text-muted fw-normal">
                <i class="fa-solid fa-lock-open text-secondary me-2"></i>
                {{ $t('general_terms.not_locked') }}
              </span>
            </div>
          </div>
          <div class="d-flex align-items-center flex-wrap gap-2 mb-3">
            <router-link v-if="canEdit && !isLocked"
                         class="btn btn-primary btn-sm"
                         :to="{name: ROUTES.GENERAL_TERMS.EDIT, params: {id: generalTerm.id}}">
              <i class="fas fa-edit me-1"></i>
              {{ $t('general_terms.edit') }}
            </router-link>
            <span v-else-if="canEdit && isLocked"
                  class="btn btn-secondary btn-sm disabled locked-edit-btn"
                  :title="$t('general_terms.locked_edit_hint')">
              <i class="fas fa-edit me-1"></i>
              {{ $t('general_terms.edit') }}
            </span>
            <button v-if="canEdit && !isLocked"
                    class="btn btn-warning btn-sm"
                    type="button"
                    :disabled="locking"
                    @click="lockGeneralTerm">
              <i class="fa-fw fa-solid fa-lock"></i>
              {{ $t('general_terms.lock') }}
            </button>
            <button class="btn btn-outline-secondary btn-sm"
                    :class="{active: showMarkdownPreview}"
                    type="button"
                    :aria-pressed="showMarkdownPreview"
                    @click="showMarkdownPreview = !showMarkdownPreview">
              <i class="fa-fw fa-solid fa-file-lines"></i>
              {{ $t('general_terms.markdown_preview') }}
            </button>
            <button v-if="renderedPdf"
                    class="btn btn-outline-secondary btn-sm"
                    :class="{active: showPdfPreview}"
                    type="button"
                    :aria-pressed="showPdfPreview"
                    @click="showPdfPreview = !showPdfPreview">
              <i class="fa-fw fa-solid fa-file-pdf"></i>
              {{ $t('general_terms.pdf_preview') }}
            </button>
            <button v-if="renderedPdf"
                    class="btn btn-primary btn-sm"
                    type="button"
                    @click="downloadPdf">
              <i class="fa-fw fa-solid fa-file-pdf"></i>
              {{ $t('general_terms.download_pdf') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 mt-4" v-if="showMarkdownPreview || showPdfPreviewPanel">
      <div class="row g-4 align-items-start">
        <div :class="markdownPreviewColumnClass" v-if="showMarkdownPreview">
          <div class="card">
            <div class="card-header">
              {{ $t('general_terms.content') }}
            </div>
            <div class="card-body">
              <pre class="general-term-content mb-0">{{ contentMarkdown }}</pre>
            </div>
          </div>
        </div>

        <div :class="pdfPreviewColumnClass" v-if="showPdfPreviewPanel">
          <div class="card">
            <div class="card-header">
              {{ $t('general_terms.pdf_preview') }}
            </div>
            <FilePreview :src="renderedPdf"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {useGeneralTermStore} from "@/stores/general_term";
import {useUserStore} from "@/stores/user";
import {PERMISSIONS, ROUTES} from "@/scripts/constants";
import ShowRow from "@/components/ShowRow.vue";
import FilePreview from "@/components/FilePreview.vue";
import {Helpers} from "@/helpers";

export default {
  name: 'GeneralTermsShow',
  components: {
    ShowRow,
    FilePreview,
  },
  setup() {
    return {
      generalTermStore: useGeneralTermStore(),
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      locking: false,
      showMarkdownPreview: true,
      showPdfPreview: true,
    }
  },
  computed: {
    ROUTES() {
      return ROUTES
    },
    generalTerm() {
      return this.generalTermStore.general_term
    },
    renderedPdf() {
      return this.generalTermStore.renderedPdf
    },
    showPdfPreviewPanel() {
      return this.showPdfPreview && Boolean(this.renderedPdf)
    },
    markdownPreviewColumnClass() {
      return this.showPdfPreviewPanel ? 'col-12 col-xl-5' : 'col-12'
    },
    pdfPreviewColumnClass() {
      return this.showMarkdownPreview ? 'col-12 col-xl-7' : 'col-12'
    },
    canEdit() {
      return this.userStore.user.hasPermission(PERMISSIONS.CONTRACTS.EDIT)
    },
    isLocked() {
      return Boolean(this.generalTerm?.locked_at)
    },
    lockedAtLabel() {
      return this.formatDate(this.generalTerm?.locked_at, 'datetime') || '-'
    },
    contentMarkdown() {
      const content = this.generalTerm?.content
      if (typeof content === 'string') {
        return content
      }
      if (content && typeof content === 'object') {
        return JSON.stringify(content, null, 2)
      }
      return ''
    },
  },
  created() {
    this.generalTermStore.show(this.$route.params.id).then(() => {
      this.setTitle(this.generalTerm.title)
      this.showMarkdownPreview = !this.isLocked
      this.showPdfPreview = !this.isLocked
    })
  },
  methods: {
    downloadPdf() {
      this.generalTermStore.downloadPdf()
    },
    lockGeneralTerm() {
      this.locking = true
      this.generalTermStore.lock(this.$route.params.id).then(() => {
        this.showMarkdownPreview = false
        this.showPdfPreview = false
        Helpers.emitter.emit('success', this.$t('general_terms.locked'))
      }).finally(() => {
        this.locking = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.locked-badge {
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.45em 0.75em;
}

.locked-edit-btn {
  opacity: 0.65;
  cursor: not-allowed;
}

.locked-at-value {
  display: inline-flex;
  align-items: center;
}

.general-term-content {
  white-space: pre-wrap;
  font-family: inherit;
}
</style>
